import { useState, type ReactNode } from "react";

export interface FilmFilters {
  genres: string[];
  years: number[];
  ratings: string[];
  tags: string[];
}

interface FilterPanelProps {
  genreOptions: string[];
  yearOptions: number[];
  tagOptions: string[];
  filters: FilmFilters;
  onToggle: (group: keyof FilmFilters, value: string | number) => void;
  onClear: () => void;
  activeCount: number;
}

const ratingOptions = ["9.0 分以上", "8.5 分以上", "8.0 分以上"];

function FilterGroup({
  title,
  children,
}: {
  title: string;
  children: ReactNode;
}) {
  return (
    <div className="mb-6 last:mb-0">
      <h3 className="text-sm font-label font-medium text-foreground-950 mb-3">{title}</h3>
      <div className="flex flex-wrap gap-2">{children}</div>
    </div>
  );
}

function Chip({
  active,
  label,
  onClick,
}: {
  active: boolean;
  label: string;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`px-3 py-1.5 rounded-full text-xs font-label transition-colors cursor-pointer whitespace-nowrap ${
        active
          ? "bg-accent-500 text-background-50"
          : "bg-background-200/60 text-foreground-700 hover:bg-background-300/60 hover:text-foreground-950"
      }`}
    >
      {label}
    </button>
  );
}

export default function FilterPanel({
  genreOptions,
  yearOptions,
  tagOptions,
  filters,
  onToggle,
  onClear,
  activeCount,
}: FilterPanelProps) {
  const [open, setOpen] = useState(false);

  return (
    <aside className="w-full lg:w-[280px] shrink-0">
      <div className="lg:hidden mb-4">
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          className="w-full flex items-center justify-between px-4 py-3 rounded-md bg-background-100 border border-background-200/70 text-sm font-label text-foreground-900 cursor-pointer"
        >
          <span className="inline-flex items-center gap-2">
            <i className="ri-filter-3-line" />
            筛选条件
            {activeCount > 0 && (
              <span className="px-2 py-0.5 rounded-full text-[11px] bg-accent-500 text-background-50">
                {activeCount}
              </span>
            )}
          </span>
          <i className={open ? "ri-arrow-up-s-line" : "ri-arrow-down-s-line"} />
        </button>
      </div>

      <div
        className={`${open ? "block" : "hidden"} lg:block p-5 rounded-lg bg-background-100 border border-background-200/70`}
      >
        <div className="flex items-center justify-between mb-5">
          <h2 className="font-heading font-semibold text-foreground-950">筛选</h2>
          {activeCount > 0 && (
            <button
              type="button"
              onClick={onClear}
              className="text-xs font-label text-accent-500 hover:text-accent-600 transition-colors cursor-pointer"
            >
              清空筛选
            </button>
          )}
        </div>

        <FilterGroup title="影片类型">
          {genreOptions.map((genre) => (
            <Chip
              key={genre}
              label={genre}
              active={filters.genres.includes(genre)}
              onClick={() => onToggle("genres", genre)}
            />
          ))}
        </FilterGroup>

        <FilterGroup title="上映年份">
          {yearOptions.map((year) => (
            <Chip
              key={year}
              label={`${year}`}
              active={filters.years.includes(year)}
              onClick={() => onToggle("years", year)}
            />
          ))}
        </FilterGroup>

        <FilterGroup title="评分区间">
          {ratingOptions.map((rating) => (
            <Chip
              key={rating}
              label={rating}
              active={filters.ratings.includes(rating)}
              onClick={() => onToggle("ratings", rating)}
            />
          ))}
        </FilterGroup>

        <FilterGroup title="特色标签">
          {tagOptions.map((tag) => (
            <Chip
              key={tag}
              label={tag}
              active={filters.tags.includes(tag)}
              onClick={() => onToggle("tags", tag)}
            />
          ))}
        </FilterGroup>
      </div>
    </aside>
  );
}